import React, { useCallback, useState } from 'react';
import {
  AlertBannerProvider,
  StackingAlertBanner,
} from '@saviynt/design-system';
import cx from 'classnames';

import { ERROR_ALERT_AUTO_DISMISS_MS } from '../../../../shared/constants';
import { useInvertedLocalize } from '../../../../shared/hooks';

import { localPreviewAlertsMessages } from './messages';

import styles from './LocalPreviewAlerts.module.css';

export const PREVIEW_BANNERS = {
  REQUIREMENTS_NOT_MET: 'requirements-not-met',
  SUBMIT_SUCCESS: 'submit-success',
  SUBMIT_ERROR: 'submit-error',
  SUBMIT_NO_DATA: 'submit-no-data',
  UPDATE_ENTITY_ERROR: 'update-entity-error',
  TERMS_LINK_EMPTY: 'tnc-link-empty',
} as const;

export type PreviewBannerType =
  (typeof PREVIEW_BANNERS)[keyof typeof PREVIEW_BANNERS];

interface BannerState {
  isVisible: boolean;
  message?: string;
}

export function useLocalAlertBanners() {
  const [requirementsNotMet, setRequirementsNotMet] = useState<BannerState>({
    isVisible: false,
  });
  const [submitSuccess, setSubmitSuccess] = useState<BannerState>({
    isVisible: false,
  });
  const [submitError, setSubmitError] = useState<BannerState>({
    isVisible: false,
  });
  const [submitNoData, setSubmitNoData] = useState<BannerState>({
    isVisible: false,
  });
  const [updateEntityError, setUpdateEntityError] = useState<BannerState>({
    isVisible: false,
  });
  const [termsLinkEmpty, setTermsLinkEmpty] = useState<BannerState>({
    isVisible: false,
  });

  const showBanner = useCallback(
    (type: PreviewBannerType, message?: string) => {
      const bannerState = { isVisible: true, message };

      switch (type) {
        case PREVIEW_BANNERS.REQUIREMENTS_NOT_MET:
          setRequirementsNotMet(bannerState);
          setSubmitNoData({ isVisible: false }); // Hide no data banner
          break;
        case PREVIEW_BANNERS.SUBMIT_SUCCESS:
          setSubmitSuccess(bannerState);
          break;
        case PREVIEW_BANNERS.SUBMIT_ERROR:
          setSubmitError(bannerState);
          break;
        case PREVIEW_BANNERS.SUBMIT_NO_DATA:
          setSubmitNoData(bannerState);
          setRequirementsNotMet({ isVisible: false }); // Hide Requirment banner
          break;
        case PREVIEW_BANNERS.UPDATE_ENTITY_ERROR:
          setUpdateEntityError(bannerState);
          break;
        case PREVIEW_BANNERS.TERMS_LINK_EMPTY:
          setTermsLinkEmpty(bannerState);
          break;
        default:
          break;
      }
    },
    []
  );

  const hideBanner = useCallback((type: PreviewBannerType) => {
    switch (type) {
      case PREVIEW_BANNERS.REQUIREMENTS_NOT_MET:
        setRequirementsNotMet({ isVisible: false });
        break;
      case PREVIEW_BANNERS.SUBMIT_SUCCESS:
        setSubmitSuccess({ isVisible: false });
        break;
      case PREVIEW_BANNERS.SUBMIT_ERROR:
        setSubmitError({ isVisible: false });
        break;
      case PREVIEW_BANNERS.SUBMIT_NO_DATA:
        setSubmitNoData({ isVisible: false });
        break;
      case PREVIEW_BANNERS.UPDATE_ENTITY_ERROR:
        setUpdateEntityError({ isVisible: false });
        break;
      case PREVIEW_BANNERS.TERMS_LINK_EMPTY:
        setTermsLinkEmpty({ isVisible: false });
        break;
      default:
        break;
    }
  }, []);

  const hideNoDataBanner = useCallback(() => {
    setSubmitNoData({ isVisible: false });
  }, []);

  const resetBanners = useCallback(() => {
    setRequirementsNotMet({ isVisible: false });
    setSubmitSuccess({ isVisible: false });
    setSubmitError({ isVisible: false });
    setSubmitNoData({ isVisible: false });
    setUpdateEntityError({ isVisible: false });
    setTermsLinkEmpty({ isVisible: false });
  }, []);

  return {
    banners: {
      requirementsNotMet,
      submitSuccess,
      submitError,
      submitNoData,
      updateEntityError,
      termsLinkEmpty,
    },
    showBanner,
    hideBanner,
    resetBanners,
    hideNoDataBanner,
  };
}

function LocalPreviewAlerts(props: {
  banners: {
    requirementsNotMet: BannerState;
    submitSuccess: BannerState;
    submitError: BannerState;
    submitNoData: BannerState;
    updateEntityError: BannerState;
    termsLinkEmpty: BannerState;
  };
  onDismiss: (type: PreviewBannerType) => void;
}) {
  const l = useInvertedLocalize(localPreviewAlertsMessages);

  const { banners, onDismiss } = props;

  return (
    <AlertBannerProvider
      className={cx('FF-AlertBannerProvider', styles.localPreviewAlerts)}>
      <StackingAlertBanner
        colorTheme='Critical'
        title={l('Action Required')}
        description={
          banners.requirementsNotMet.message ??
          l('Please review and fix the highlighted errors before submitting.')
        }
        isVisible={banners.requirementsNotMet.isVisible}
        onCancel={() => onDismiss(PREVIEW_BANNERS.REQUIREMENTS_NOT_MET)}
        shouldAutoDismiss
        autoDismissDelay={ERROR_ALERT_AUTO_DISMISS_MS}
        className='FF-LocalPreviewAlerts-alert'
      />

      <StackingAlertBanner
        colorTheme='Success'
        title={l('Submission Complete')}
        description={
          banners.submitSuccess.message ?? l('Your request has been submitted.')
        }
        isVisible={banners.submitSuccess.isVisible}
        onCancel={() => onDismiss(PREVIEW_BANNERS.SUBMIT_SUCCESS)}
        shouldAutoDismiss
        className='FF-LocalPreviewAlerts-alert'
      />

      <StackingAlertBanner
        colorTheme='Critical'
        title={l('Submission Failed')}
        description={
          banners.submitError.message ??
          l('We could not submit your request. Please try again.')
        }
        isVisible={banners.submitError.isVisible}
        onCancel={() => onDismiss(PREVIEW_BANNERS.SUBMIT_ERROR)}
        shouldAutoDismiss
        autoDismissDelay={ERROR_ALERT_AUTO_DISMISS_MS}
        className='FF-LocalPreviewAlerts-alert'
      />

      <StackingAlertBanner
        colorTheme='Warning'
        title={l('Nothing To Submit')}
        description={
          banners.submitNoData.message ?? l('There is no data to submit.')
        }
        isVisible={banners.submitNoData.isVisible}
        onCancel={() => onDismiss(PREVIEW_BANNERS.SUBMIT_NO_DATA)}
        shouldAutoDismiss
        autoDismissDelay={ERROR_ALERT_AUTO_DISMISS_MS}
        className='FF-LocalPreviewAlerts-alert'
      />

      <StackingAlertBanner
        colorTheme='Critical'
        title={l('Failed to Load Entity Data')}
        description={
          banners.updateEntityError.message ??
          l('We could not load the entity data. Please try again.')
        }
        isVisible={banners.updateEntityError.isVisible}
        onCancel={() => onDismiss(PREVIEW_BANNERS.UPDATE_ENTITY_ERROR)}
        shouldAutoDismiss
        autoDismissDelay={ERROR_ALERT_AUTO_DISMISS_MS}
        className='FF-LocalPreviewAlerts-alert'
      />

      <StackingAlertBanner
        colorTheme='Critical'
        title={l('Failed to Open Link')}
        description={
          banners.termsLinkEmpty.message ??
          l('Please edit the Terms and Conditions link and try again.')
        }
        isVisible={banners.termsLinkEmpty.isVisible}
        onCancel={() => onDismiss(PREVIEW_BANNERS.TERMS_LINK_EMPTY)}
        shouldAutoDismiss
        autoDismissDelay={ERROR_ALERT_AUTO_DISMISS_MS}
        className='FF-LocalPreviewAlerts-alert'
      />
    </AlertBannerProvider>
  );
}

export default LocalPreviewAlerts;
